import React, { useEffect, useState } from "react";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BaseDialog from "../../../utils/dialog/BaseDialog";

export default function CorporateGroupSaveDialog({
  open,
  onClose,
  onSave,
  group,
  selected,
  saving,
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(group?.name || "");
    setError("");
  }, [group, open]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Enter a name for this corporate group.");
      return;
    }
    const result = await onSave(trimmedName, group?.id || "");
    if (result?.ok) onClose(result.group);
    else setError(result?.error || "Unable to save the corporate group.");
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      closeDisabled={saving}
      title={group ? "Update corporate group" : "Save corporate group"}
      subtitle="Reuse this company list in another Group Relief case."
      icon={<BusinessOutlinedIcon />}
      maxWidth="sm"
      actions={
        <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
          <Button
            fullWidth
            variant="outlined"
            disabled={saving}
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveOutlinedIcon />
              )
            }
            onClick={handleSave}
          >
            {saving ? "Saving..." : group ? "Save changes" : "Save group"}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2.5}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          required
          label="Corporate group name"
          value={name}
          inputProps={{ maxLength: 80 }}
          helperText='Use a familiar name, for example "ABC Holdings".'
          onChange={(event) => {
            setName(event.target.value);
            setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !saving) handleSave();
          }}
        />
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Typography fontWeight={700} sx={{ mb: 0.5 }}>
            {selected.length} companies in this group
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, lineHeight: 1.6 }}
          >
            Companies saved in Tax Savy can be selected again in future cases.
            External companies will need to be uploaded again when creating a
            new case.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {selected.map((company) => (
              <Chip
                key={company.id}
                label={`${company.name}${company.isExternal ? " - EXTERNAL" : ""}`}
                size="small"
                color={company.isExternal ? "secondary" : "default"}
                variant={company.isExternal ? "outlined" : "filled"}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </BaseDialog>
  );
}
